import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import {
  customEmailValidator,
  noWhitespaceValidator,
  trnValidator,
} from '../../shared/services/validations';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { GenderOptionsEnum } from '../../../assets/enum/genderOptionsEnum';
import { preferenceOptionsEnum } from '../../../assets/enum/preferenceTypesEnum';
import { CurrencyAdjustmentService } from '../../features/currency-adjustment/services/currency-adjustment.service';
import { VendorService } from '../../features/vendors/services/vendor.service';

@Component({
  selector: 'app-vendor-new',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    ReactiveFormsModule,
    FormsModule,
    MatDatepickerModule,
  ],
  templateUrl: './vendor-new.component.html',
  styleUrl: './vendor-new.component.scss',
})
export class VendorNewComponent {
  newVendorForm: FormGroup;
  vendorData: any = {};
  vendorId: any;
  validationError: boolean = false;
  documentArray: any[] = [];
  public genderData: any[] = [];
  public preferenceData: any[] = [];
  today: Date = new Date();
  currencyData: any;

  constructor(
    private fb: FormBuilder,
    private vendorService: VendorService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private currencyService: CurrencyAdjustmentService
  ) {}

  ngOnInit() {
    this.initForm();
    this.fetchCurrencies();
    this.route.paramMap.subscribe((params) => {
      this.vendorId = params.get('id');
      if (this.vendorId) {
        this.fetchVendorById();
      }
    });
  }

  // address String
  // openingBalance Float

  initForm() {
    if (this.vendorData) {
      this.newVendorForm = this.fb.group({
        id: Number(this.vendorId),
        vendorName: [
          this.vendorData?.vendorName || '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        contactPerson: [
          this.vendorData?.contactPerson || '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        email: [
          this.vendorData.email || '',
          [Validators.required, customEmailValidator()],
        ],
        trn: [this.vendorData.trn || '', [Validators.required]],
        phone: [
          this.vendorData.phone || '',
          [
            Validators.required,
            Validators.minLength(8), // Minimum length for phone number
            Validators.maxLength(15), // Maximum length for phone number
            Validators.pattern('^[0-9]+$'), // Only allows numeric values
          ],
        ],
        address: [this.vendorData.address || '', [Validators.required]],
        currency: [this.vendorData.currency || '', [Validators.required]],
        openingBalance: [
          this.vendorData.openingBalance || '',
          [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$')],
        ],
      });
    } else {
      this.newVendorForm = this.fb.group({
        vendorName: [
          '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        contactPerson: [
          '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        email: [
          '',
          [
            Validators.required,
            customEmailValidator(),
            Validators.maxLength(50),
          ],
        ],
        phone: [
          '',
          [
            Validators.required,
            Validators.minLength(8), // Minimum length for phone number
            Validators.maxLength(15), // Maximum length for phone number
            Validators.pattern('^[0-9]+$'), // Only allows numeric values
          ],
        ],
        trn: ['', [Validators.required]],
        address: ['', Validators.required],
        currency: ['', Validators.required],
        openingBalance: [
          '',
          [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$')],
        ],
      });
    }
  }

  fetchCurrencies() {
    this.currencyService.fetchCurrencies('', {}).subscribe({
      next: (currency) => {
        this.currencyData = currency;
      },
      error: (error) => console.error(error),
    });
  }

  fetchVendorById() {
    this.vendorService.fetchVendorById(Number(this.vendorId)).subscribe({
      next: (data) => {
        this.vendorData = data;
        this.initForm();
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  async onSubmit() {
    if (this.newVendorForm.invalid) {
      this.newVendorForm.markAllAsTouched();
      return;
    }
    const formValue = this.newVendorForm.value;
    const branchId = localStorage.getItem('selectedBranchId');

    const createVendorInput = {
      vendorName: formValue.vendorName,
      contactPerson: formValue.contactPerson,
      email: formValue.email,
      phone: formValue.phone,
      address: formValue.address,
      trn: formValue.trn,
      currency: formValue.currency,
      openingBalance: parseFloat(formValue.openingBalance) || 0,
      branchId: Number(branchId),
    };

    const updateVendorInput = {
      id: Number(this.vendorId),
      vendorName: formValue.vendorName,
      contactPerson: formValue.contactPerson,
      email: formValue.email,
      phone: formValue.phone,
      address: formValue.address,
      trn: formValue.trn,
      currency: formValue.currency,
      openingBalance: parseFloat(formValue.openingBalance) || 0,
      branchId: Number(branchId),
    };

    if (this.vendorId) {
      this.vendorService
        .updateVendor(updateVendorInput, Number(this.vendorId))
        .subscribe({
          next: async (data) => {
            this.toastr.success('Vendor updated successfully', 'Success');
            this.onCancel();
            this.router.navigate([`/vendors`]);
          },
          error: (error) => this.toastr.error(error, 'Error'),
        });
    } else {
      this.vendorService.createVendor(createVendorInput).subscribe({
        next: async (data) => {
          this.toastr.success('Vendor created successfully', 'Success');
          this.onCancel();
          this.router.navigate([`/vendors`]);
        },
        error: (error) => this.toastr.error(error, 'Error'),
      });
    }
  }

  onCancel() {
    if (!this.vendorId) {
      // this.newVendorForm.reset();
      // this.addressComponent.onCancel();
      // this.customerDocumentsComponent.onCancel();
      this.router.navigate([`/vendors`]);
    } else {
      this.router.navigate([`/vendors`]);
    }
  }
}
